import { useState, useRef, useEffect } from "react";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import BudVision from "@/components/BudVision";
import { useStateCtx } from "@/context/StateContext";
import { concierge } from "@/lib/api";
import { Sparkles, Send, Cpu, Loader2, Mic, MicOff, MessageSquare, Eye } from "lucide-react";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const SUGGESTIONS = [
  "Find me gas under $40, indica-leaning",
  "Best value vape for focus",
  "Sleeper pick for sleep?",
  "Top shelf with citrus",
  "Cheapest live rosin under $50",
];
const MODELS = [
  { v: "claude", l: "Claude 4.5", desc: "Sommelier" },
  { v: "gpt", l: "GPT-5.2", desc: "Sharpest" },
  { v: "gemini", l: "Gemini 3", desc: "Fastest" },
];

export default function Concierge() {
  const { stateCode } = useStateCtx();
  const [tab, setTab] = useState("chat"); // chat | vision
  const [model, setModel] = useState("claude");
  const [messages, setMessages] = useState([
    { who: "ai", text: "Yo. Bud here, my dude. Tell me a vibe, a budget, or a flavor — I'll pull straight gas from your in-state catalog. 🌿💨" },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [listening, setListening] = useState(false);
  const [voiceSupported, setVoiceSupported] = useState(false);
  const sessionId = useRef(`session-${Date.now()}`);
  const scrollRef = useRef(null);
  const recogRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  // Web Speech API setup
  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setVoiceSupported(false);
      return;
    }
    setVoiceSupported(true);
    const r = new SR();
    r.lang = "en-US";
    r.interimResults = true;
    r.continuous = false;
    r.onresult = (e) => {
      const txt = Array.from(e.results).map((res) => res[0].transcript).join("");
      setInput(txt);
      if (e.results[e.results.length - 1].isFinal) {
        setListening(false);
        setTimeout(() => ask(txt), 120);
      }
    };
    r.onerror = () => setListening(false);
    r.onend = () => setListening(false);
    recogRef.current = r;
    return () => { try { r.stop(); } catch (e) { /* ignore */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleMic = () => {
    if (!recogRef.current) return;
    if (listening) {
      try { recogRef.current.stop(); } catch (e) { /* ignore */ }
      setListening(false);
    } else {
      setInput("");
      try { recogRef.current.start(); setListening(true); } catch (e) { /* ignore */ }
    }
  };

  const ask = async (text) => {
    const q = (text ?? input).trim();
    if (!q) return;
    setMessages((m) => [...m, { who: "user", text: q }]);
    setInput("");
    setBusy(true);
    try {
      const res = await concierge.chat({ message: q, session_id: sessionId.current, model, state: stateCode === "ALL" ? "CO" : stateCode });
      setMessages((m) => [...m, { who: "ai", text: res.reply, fallback: res.fallback }]);
    } catch (e) {
      const msg = e?.code === "ECONNABORTED"
        ? "Bud took too long. Try a shorter prompt or hit me again."
        : (e?.response?.data?.detail || "Bud's offline, my dude. Hit me again.");
      setMessages((m) => [...m, { who: "ai", text: msg, fallback: true }]);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex flex-col relative overflow-hidden">
      <div className="blob" style={{ width: 380, height: 380, background: "#15FF73", top: -100, right: -80 }} />
      <div className="blob" style={{ width: 280, height: 280, background: "#FF1A8C", bottom: 100, left: -60, animationDelay: "-8s" }} />
      <NavBar />
      <div className="max-w-4xl w-full mx-auto px-5 lg:px-10 py-12 flex-1 flex flex-col relative">
        <div className="flex items-end justify-between mb-7 gap-4 flex-wrap">
          <div>
            <span className="sticker sticker-green mb-3 inline-block"><Sparkles size={11} strokeWidth={2.4} /> Bud · {stateCode}</span>
            <h1 className="font-display text-5xl sm:text-6xl font-extrabold tracking-tight leading-[0.92] mt-3">Tell Bud a <span className="text-rainbow">vibe.</span></h1>
          </div>
          {tab === "chat" && (
            <div className="flex items-center gap-2">
              <Cpu size={14} className="opacity-60" />
              <Select value={model} onValueChange={setModel}>
                <SelectTrigger data-testid="model-select" className="w-[180px] rounded-full border-2 border-[#0B0F0C] bg-[#FAF6E8] h-11 font-bold">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-2xl border-2 border-[#0B0F0C] bg-[#FAF6E8]">
                  {MODELS.map((m) => <SelectItem key={m.v} value={m.v} className="rounded-lg">
                    <div className="flex flex-col"><span className="font-bold">{m.l}</span><span className="text-[10px] text-[#0B0F0C]/55 font-mono-x">{m.desc}</span></div>
                  </SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Tab switcher */}
        <div className="flex gap-2 mb-5">
          <button
            data-testid="tab-chat"
            onClick={() => setTab("chat")}
            className={`btn-x ${tab === "chat" ? "btn-primary" : "btn-ghost"} !py-2 !px-4 !text-sm`}
          >
            <MessageSquare size={13} strokeWidth={2.4} /> Chat with Bud
          </button>
          <button
            data-testid="tab-vision"
            onClick={() => setTab("vision")}
            className={`btn-x ${tab === "vision" ? "btn-primary" : "btn-ghost"} !py-2 !px-4 !text-sm`}
          >
            <Eye size={13} strokeWidth={2.4} /> Show Bud
          </button>
        </div>

        {tab === "vision" ? (
          <BudVision />
        ) : (
        <>
        <div ref={scrollRef} className="flex-1 card-x card-cyan bg-[#FAF6E8] p-6 overflow-auto min-h-[420px] max-h-[60vh]">
          <div className="space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={m.who === "user" ? "flex justify-end" : "flex"}>
                {m.who === "user" ? (
                  <div data-testid={`msg-user-${i}`} className="bg-[#0B0F0C] text-[#F5F0DC] px-4 py-3 rounded-2xl rounded-tr-sm max-w-[80%] text-sm font-medium">{m.text}</div>
                ) : (
                  <div data-testid={`msg-ai-${i}`} className="bg-[#15FF73] text-[#0B0F0C] px-4 py-3 rounded-2xl rounded-tl-sm max-w-[88%] text-sm leading-relaxed whitespace-pre-line border-2 border-[#0B0F0C]">
                    {m.text}
                    {m.fallback && <div className="mt-2 text-[10px] font-mono-x uppercase tracking-widest opacity-70">offline · catalog match</div>}
                    {i > 0 && (
                      <div className="mt-3 pt-3 border-t-2 border-dashed border-[#0B0F0C]/25">
                        <BudSpeak text={m.text} showPicker={false} compact />
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
            {busy && (
              <div className="flex">
                <div className="bg-[#15FF73] border-2 border-[#0B0F0C] px-4 py-3 rounded-2xl rounded-tl-sm text-sm inline-flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin" /> Bud's rolling up…
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-5">
          <div className="flex flex-wrap gap-2 mb-3">
            {SUGGESTIONS.map((s, i) => (
              <button key={i} data-testid={`suggestion-${i}`} onClick={() => ask(s)}
                className="text-xs font-mono-x font-bold uppercase tracking-widest px-3 py-1.5 rounded-full border-2 border-[#0B0F0C] hover:bg-[#FFE74C] transition-all">{s}</button>
            ))}
          </div>
          <div className="flex items-center gap-2 rounded-full border-2 border-[#0B0F0C] bg-[#FAF6E8] focus-within:bg-white focus-within:shadow-[3px_3px_0_#0B0F0C] px-5 h-14 transition-all">
            <input
              data-testid="concierge-input" value={input}
              onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask()}
              placeholder={listening ? "Listening… speak now" : "Try: best gas live rosin under $50…"}
              className="flex-1 bg-transparent outline-none text-sm placeholder:text-[#0B0F0C]/45 font-medium"
            />
            {voiceSupported && (
              <button
                data-testid="concierge-mic"
                onClick={toggleMic}
                title={listening ? "Stop listening" : "Voice search"}
                className={`rounded-full border-2 border-[#0B0F0C] w-10 h-10 flex items-center justify-center transition-all ${listening ? "bg-[#FF1A8C] text-white animate-pulse" : "bg-[#FAF6E8] hover:bg-[#FFE74C]"}`}
              >
                {listening ? <MicOff size={14} strokeWidth={2.4} /> : <Mic size={14} strokeWidth={2.4} />}
              </button>
            )}
            <button data-testid="concierge-send" onClick={() => ask()} disabled={busy} className="btn-x btn-primary !py-2 !px-4 !text-xs disabled:opacity-50">
              <Send size={12} strokeWidth={2.4} /> Ask
            </button>
          </div>
        </div>
        </>
        )}
      </div>
      <Footer />
    </div>
  );
}
